<script setup>
import { computed } from 'vue';

const props = defineProps({
    text: {
        type: String,
        default: '',
    },
    linkClass: {
        type: String,
        default: 'mf-link',
    },
    openInNewTab: {
        type: Boolean,
        default: true,
    },
});

const segments = computed(() => buildSegments(props.text ?? ''));

function buildSegments(text) {
    if (! text) {
        return [];
    }

    const matches = [];
    const urlPattern = /((?:https?:\/\/|www\.)[^\s<>'\"]+|mailto:[^\s<>'\"]+)/gi;
    let match;

    // Collect matches first to avoid running the regex repeatedly during replacement.
    while ((match = urlPattern.exec(text)) !== null) {
        matches.push({ value: match[0], index: match.index });
    }

    if (! matches.length) {
        return [{ type: 'text', content: text }];
    }

    const segments = [];
    let lastIndex = 0;

    matches.forEach((match) => {
        const { value, index } = match;

        if (index > lastIndex) {
            segments.push({ type: 'text', content: text.slice(lastIndex, index) });
        }

        const { linkText, trailing } = trimTrailingPunctuation(value);

        if (linkText.length === 0) {
            // Nothing left after trimming (unlikely), push the original as text
            segments.push({ type: 'text', content: value });
        } else {
            segments.push({
                type: 'link',
                content: linkText,
                href: toHref(linkText),
            });
        }

        if (trailing) {
            segments.push({ type: 'text', content: trailing });
        }

        lastIndex = index + value.length;
    });

    if (lastIndex < text.length) {
        segments.push({ type: 'text', content: text.slice(lastIndex) });
    }

    return segments;
}

function trimTrailingPunctuation(value) {
    let linkText = value;
    let trailing = '';

    const trailingPattern = /[.,!?;:]+$/;

    while (linkText.length > 0 && trailingPattern.test(linkText.slice(-1))) {
        trailing = linkText.slice(-1) + trailing;
        linkText = linkText.slice(0, -1);
    }

    return { linkText, trailing };
}

function toHref(value) {
    if (/^mailto:/i.test(value)) {
        return value;
    }

    if (/^https?:\/\//i.test(value)) {
        return value;
    }

    if (/^www\./i.test(value)) {
        return `https://${value}`;
    }

    return `https://${value}`;
}
</script>

<template>
    <span class="whitespace-break-spaces">
        <template v-for="(segment, index) in segments" :key="index">
            <span v-if="segment.type === 'text'">{{ segment.content }}</span>
            <a
                v-else
                :href="segment.href"
                :class="linkClass"
                :target="openInNewTab ? '_blank' : undefined"
                :rel="openInNewTab ? 'noopener noreferrer' : undefined"
            >
                {{ segment.content }}
            </a>
        </template>
    </span>
</template>
